import { createPortal } from 'react-dom';
import { Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface LearnMorePopupProps {
  /** Position in viewport coordinates (for fixed positioning) */
  position: { top: number; left: number };
  /** Callback when the learn more button is clicked */
  onClick: () => void;
  /** Optional additional class names */
  className?: string;
}

/**
 * A floating popup that appears when text is selected, allowing users
 * to quickly ask about the selected content.
 *
 * Rendered in a portal to avoid causing layout shifts in the parent container.
 */
export function LearnMorePopup({ position, onClick, className }: LearnMorePopupProps) {
  return createPortal(
    <div
      data-learn-more-popup
      className={cn(
        'fixed z-50 -translate-x-1/2 animate-in fade-in-0 zoom-in-95 duration-150',
        className
      )}
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <Button
        size="sm"
        variant="secondary"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }}
        className="shadow-lg gap-1.5 px-3 py-1.5 h-auto text-xs font-medium rounded-full border border-border hover:bg-secondary"
      >
        <Lightbulb className="h-3.5 w-3.5" />
        Learn more
      </Button>
    </div>,
    document.body
  );
}
